// Who an outgoing email says it is from.
//
// ── Why this is in src/domain/ and not in the transport ────────────────────
//
// Two consumers: the transport, functions/delivery/transport/sendgrid.js, and
// Business Settings → Email & SMS, which shows the studio a live From /
// Reply-To preview of what its own address will produce. A preview computed
// from a *second* copy of this rule could disagree with the sender.
//
// src/domain/ cannot import from functions/, but functions/domain/ is a
// mechanical mirror of src/domain/ (functions/scripts/sync-domain.mjs, run by
// the deploy hook and by the jest globalSetup), so one copy is reachable from
// both sides. sendgrid.js imports `envelopeFor` and `isFreeMailAddress` from
// here and re-exports them.
//
// This file is side-effect-free: it is loaded by a React panel and by a Cloud
// Function.
//
// ── The rule itself ────────────────────────────────────────────────────────
//
// MMS refuses to send *as* a free-provider address. With a gmail.com studio
// address it sends from
//
//     <teacher name> via My Music Staff <noreply+XXXXX@mymusicstaff.com>
//
// with Reply-To set to the teacher, and says so on the settings screen under
// the heading "Public Email Provider".
//
// It is a deliverability rule. gmail.com, yahoo.com and the rest publish DMARC
// records and do not authorise our ESP to send for them. A message whose
// header From says `@gmail.com`, sent through our SendGrid account, fails SPF
// and DKIM alignment, therefore DMARC, and is rejected or filed as spam at the
// *recipient* — after SendGrid has already returned 202.
//
// ── What a custom domain does (#73) ────────────────────────────────────────
//
// MMS rewrites a studio address on a custom domain too:
//
//     From:     <studio name> via My Music Staff <noreply+XXXXX@mymusicstaff.com>
//     Reply-To: <studio name> <studio@example.com>
//     DKIM-Signature: d=mymusicstaff.com; s=mms1        (dmarc=pass)
//
// and its settings screen says why: "SPF Not Configured. DKIM Not Configured.
// We can't send email directly on your behalf using studio@example.com."
//
// So the question is **"is this domain authenticated with our ESP"**, and a
// free provider is only the special case of a domain that never can be. This
// app tracks no per-studio domain authentication in SendGrid, so every studio
// address is rewritten, as MMS does: the From is deliverable and the reply
// still reaches the teacher. Per-studio domain authentication would be learned
// in this one function.
//
// FREE_MAIL_DOMAINS answers a different question: sendgrid.js refuses to start
// when the *deployment's own* MESSAGE_FROM_EMAIL is at a free provider, where
// there is nothing to rewrite to.

import { BRAND } from './brand.js';

// The display name the deployment lends its envelope when nothing says
// otherwise. functions/delivery/config.js imports this as its default for
// MESSAGE_FROM_NAME, so the preview a studio is shown in the browser and the
// envelope the function actually builds say the same words — and both say the
// product's name as design/brand.json spells it.
export const DEFAULT_SENDER_NAME = BRAND.name;

// Mailbox providers that publish a DMARC policy and will not authorise us.
//
// Not exhaustive — a denylist standing in for "is this domain one the studio
// controls". It covers the addresses a music teacher has. Every studio
// address is rewritten either way; this decides whether the deployment's own
// sender may start (see above) and how the Email Settings notice is titled.
export const FREE_MAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.com.au', 'ymail.com', 'rocketmail.com',
  'outlook.com', 'outlook.co.uk', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'live.co.uk', 'msn.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com',
  'proton.me', 'protonmail.com', 'pm.me',
  'gmx.com', 'gmx.net', 'gmx.de',
  'mail.com', 'zoho.com', 'yandex.com', 'fastmail.com',
]);

// ─── The rules are `core/src/settings.rs`'s ────────────────────────────────
//
// The domain, the free-provider check, the contact and the envelope decision
// below are adapters over the core, so the Email Settings preview on a phone
// describes the envelope the transport here builds. `FREE_MAIL_DOMAINS` above
// stays as a literal for its module-scope readers, pinned to the core's list
// by test/core/settings.wasm.test.js. What is JavaScript is `envelopeFor`
// and its refusal, the transport's.
import { coreSync } from './core.js';

const str = (v) => (v === null || v === undefined ? null : String(v));

export function domainOf(address) {
  return coreSync().domainOf(address ? String(address) : null) ?? null;
}

export function isFreeMailAddress(address) {
  return coreSync().isFreeMailAddress(address ? String(address) : null);
}

// `{ email, name }`, which is the shape SendGrid's v3 API wants. The name key
// is omitted rather than set to null, because SendGrid rejects a null name
// instead of ignoring it.
//
// Reads through `?.` rather than destructuring a defaulted parameter: callers
// pass an explicit `null` for "there is no such contact" (the settings panel
// has no deployment sender to offer), and a default only fills in for
// `undefined`.
const shaped = (c) => (c ? (c.name ? { email: c.email, name: c.name } : { email: c.email }) : null);

export function contact(one) {
  return shaped(coreSync().mailContact(str(one?.address), str(one?.name)));
}

// `Ada Okafor <ada@example.com>`, or just the address when there is no name.
// Here rather than in the settings panel, so the preview cannot render the
// envelope in a notation the mail it describes does not use.
export function formatAddress(one) {
  return coreSync().formatAddress(str(one?.email), str(one?.name));
}

// The decision, with the deployment's own address left optional.
//
// The browser does not know MESSAGE_FROM_EMAIL — it is a Cloud Functions
// environment variable and deliberately not studio data, so a teacher cannot
// change it by writing to Firestore. But every part of the decision except that
// one string is knowable client-side: *whether* the From gets rewritten, what
// display name it gets, and what the Reply-To will be. So this returns the
// envelope with `from.email` possibly null, and the settings panel says plainly
// that the address is the deployment's rather than inventing a plausible one.
//
// Returns null when there is no studio address to reason about. An address
// that is already the deployment's is sent as the studio, with no reply to
// redirect; every other one goes as the deployment, says who it is from, and
// points the reply at the teacher — the MMS shape, for the DMARC reason above.
export function sendingIdentity({ studioName, studioEmail, sender } = {}) {
  const id = coreSync().sendingIdentity(
    str(studioName), str(studioEmail), str(sender?.address), str(sender?.name), DEFAULT_SENDER_NAME,
  );
  if (!id) return null;
  const replyTo = id.replyToEmail ? shaped({ email: id.replyToEmail, name: id.replyToName }) : null;
  return id.rewritten
    ? { from: { email: id.fromEmail ?? null, name: id.fromName }, replyTo, rewritten: true }
    : { from: shaped({ email: id.fromEmail, name: id.fromName }), replyTo, rewritten: false };
}

// Thrown when there is no verified sender to send as. Its own class rather than
// the transport's SendGridError, because this module cannot know it is being
// called from a transport; sendgrid.js re-types it, so that everything leaving
// *that* module carries the `terminal` flag deliver.js reads.
export class MissingSenderError extends Error {
  constructor(message) {
    super(message);
    this.name = 'MissingSenderError';
  }
}

// The strict form the sender uses, where the deployment's verified address is
// known and required. `from` falling back to `sender` is what happens for a
// message that carries no studio identity of its own.
export function envelopeFor({ sender, from }) {
  const verified = contact(sender);
  if (!verified) {
    // Unreachable through the transport — it refuses to construct without
    // MESSAGE_FROM_EMAIL — but this function is exported and pure, and a
    // TypeError here would be a worse answer than the real one.
    throw new MissingSenderError('No sender address is configured (MESSAGE_FROM_EMAIL).');
  }
  const studio = contact(from) || verified;
  const identity = sendingIdentity({
    studioName: studio.name,
    studioEmail: studio.email,
    sender: { address: verified.email, name: verified.name },
  });
  return identity.rewritten
    ? { from: identity.from, replyTo: identity.replyTo, rewritten: true }
    // `studio`, not `identity.from`: on the untouched path they are equal, and
    // returning the caller's own contact keeps the two branches obviously the
    // same object rather than two objects that happen to match.
    : { from: studio, replyTo: null, rewritten: false };
}
